import { Trophy, Star, Globe, Award, Lock, Zap, Crown, Info } from "lucide-react";
import { useState } from "react";
import { TROPHY_DEFS, TIER_NAMES, TIER_COLORS } from "../../../utils/minicup";

const ICONS = { top_striker: Star, match_star: Trophy, globetrotter: Globe, goal_machine: Zap, streak_master: Crown, tournament_champ: Award };

function metricValue(stats, metric) {
  return stats?.[metric] ?? 0;
}

export default function MiniCupTrophies({ stats, unlocked }) {
  const [credits, setCredits] = useState(false);
  const unlockedSet = new Set((unlocked || []).map((u) => u.trophy_id));

  return (
    <div className="space-y-4" data-testid="minicup-trophies">
      {TROPHY_DEFS.map((d) => {
        const Icon = ICONS[d.category] || Trophy;
        const val = metricValue(stats, d.metric);
        const nextTier = d.tiers.find((t) => val < t);
        const nextIdx = nextTier ? d.tiers.indexOf(nextTier) : d.tiers.length;
        const prevThresh = nextIdx > 0 ? d.tiers[nextIdx - 1] : 0;
        const pct = nextTier ? Math.min(100, Math.round(((val - prevThresh) / (nextTier - prevThresh)) * 100)) : 100;
        return (
          <div key={d.category} className="rounded-xl border border-border bg-card p-3" data-testid={`trophy-row-${d.category}`}>
            <div className="mb-2 flex items-center gap-2">
              <Icon size={18} className="wc-gold" />
              <div className="flex-1">
                <div className="text-sm font-extrabold">{d.title}</div>
                <div className="text-[11px] text-muted-foreground">{d.desc}</div>
              </div>
              <div className="text-xs font-bold text-muted-foreground">{val}</div>
            </div>
            <div className="mb-2 grid grid-cols-4 gap-2">
              {d.tiers.map((threshold, i) => {
                const id = `${d.category}_${i + 1}`;
                const got = unlockedSet.has(id) || val >= threshold;
                return (
                  <div key={id} className="flex flex-col items-center gap-1 rounded-lg border border-border/60 py-2"
                    style={got ? { background: `${TIER_COLORS[i]}22`, borderColor: `${TIER_COLORS[i]}66` } : {}}
                    data-testid={`trophy-tier-${id}`}>
                    <div className="relative">
                      <Icon size={20} style={{ color: got ? TIER_COLORS[i] : "#475569" }} />
                      {!got && <Lock size={10} className="absolute -right-1 -top-1 text-slate-500" />}
                    </div>
                    <span className="text-[9px] font-bold" style={{ color: got ? TIER_COLORS[i] : "#64748b" }}>{TIER_NAMES[i]}</span>
                    <span className="text-[9px] text-muted-foreground">{threshold}</span>
                  </div>
                );
              })}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700/50">
              <div className="h-full rounded-full wc-bg-gold" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-1 text-right text-[10px] text-muted-foreground">
              {nextTier ? `${val} / ${nextTier}` : "All unlocked!"}
            </div>
          </div>
        );
      })}

      <button onClick={() => setCredits(true)} className="mx-auto flex items-center gap-1 text-xs text-muted-foreground underline" data-testid="minicup-credits-link">
        <Info size={12} /> Credits
      </button>

      {credits && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setCredits(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5" onClick={(e) => e.stopPropagation()} data-testid="minicup-credits-modal">
            <h3 className="mb-3 text-lg font-extrabold">Credits</h3>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li>Goal Post by Nyiltemp on IconScout</li>
              <li>Soccer Ball animation by Google Inc.</li>
              <li>Sound effects from Pixabay</li>
              <li>Game art generated with AI</li>
            </ul>
            <button onClick={() => setCredits(false)} className="mt-4 w-full rounded-lg wc-bg-gold py-2 text-sm font-extrabold text-slate-900">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
